import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchFarmfolioGlobal, fetchFarmfolioGlobalScheduled, fetchFarmfolioLayups } from '../../lib/queryFunctions';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertTriangle, CheckCircle, Calendar, BookOpen, Globe, Plus,
  Search, X, Pill, Trash2, Pencil, Download, FileText,
} from 'lucide-react';import { toast } from 'sonner';
import api from '../../services/api';
import SidebarLayout from '../../components/layout/SidebarLayout';
import CareCatalogPanel from '../../components/farmfolio/CareCatalogPanel';
import { FREQ_OPTIONS, FREQ_DEFAULTS, FrequencyConfig } from '../../components/farmfolio/CareCatalogPanel';
import CareLogTable from '../../components/farmfolio/CareLogTable';
import QuickLogModal from '../../components/farmfolio/QuickLogModal';
import TurnoutLogModal from '../../components/farmfolio/TurnoutLogModal';
import TravelLogModal from '../../components/farmfolio/TravelLogModal';
import StatsLogModal from '../../components/farmfolio/StatsLogModal';
import LinkedNoteDialog from '../../components/farmfolio/LinkedNoteDialog';
import LayupModal from '../../components/farmfolio/LayupModal';
import ScheduleCareModal from '../../components/farmfolio/ScheduleCareModal';
import BulkEditScheduledModal from '../../components/farmfolio/BulkEditScheduledModal';
import GenerateCareInvoiceModal from '../../components/farmfolio/GenerateCareInvoiceModal';
import CareLogEntryDetailModal from '../../components/farmfolio/CareLogEntryDetailModal';
import {
  Tablets, PillBottle, Syringe, Bandage, Cylinder, Wheat, BrushCleaning, Fence,
  Magnet, Timer, RulerDimensionLine, Caravan, MoreHorizontal, NotepadText,
} from 'lucide-react';
import ToothIcon from '../../components/farmfolio/ToothIcon';
import { useConfirm } from '../../components/common/ConfirmDialog';

const CURRENCIES = ['USD','EUR','GBP','CAD','AUD','NZD','ZAR','CHF','JPY','HKD'];

const QL_MEDICAL     = [
  { key: 'meds',      label: 'Meds Given', icon: Tablets,  icon2: PillBottle, category: 'Medical',      type: 'Medication' },
  { key: 'procedure', label: 'Procedure',  icon: Syringe,                     category: 'Medical',      type: 'Procedure' },
  { key: 'layup',     label: 'Layup',      icon: Bandage,                     category: 'Medical',      type: 'Layup', isLayup: true },
];
const QL_DAILY       = [
  { key: 'fed',       label: 'Fed',        icon: Cylinder, icon2: Wheat,      category: 'Daily Care',   type: 'Feed' },
  { key: 'groomed',   label: 'Groomed',    icon: BrushCleaning,               category: 'Daily Care',   type: 'Groom' },
  { key: 'turnout',   label: 'Turned Out', icon: Fence,                       category: 'Daily Care',   type: 'Turnout' },
];
const QL_MAINTENANCE = [
  { key: 'farrier',   label: 'Farrier',    icon: Magnet,                      category: 'Maintenance',  type: 'Farrier' },
  { key: 'dental',    label: 'Dental',     icon: ToothIcon,                   category: 'Maintenance',  type: 'Dental' },
];
const QL_CONDITIONING = { key: 'worked', label: 'Worked', icon: Timer,  category: 'Conditioning', type: 'Exercise' };
const QL_MISC        = [
  { key: 'stats',     label: 'Stats Taken',icon: RulerDimensionLine,          category: 'Stats',        type: 'Vitals' },
  { key: 'travel',    label: 'Travel',     icon: Caravan,                     category: 'Travel',       type: 'Transport' },
  { key: 'other',     label: 'Other',      icon: MoreHorizontal,              category: 'Notes',        type: 'General Note' },
];

const CARE_BORDER = {
  'Medical':      'border-l-4 border-l-purple-300',
  'Daily Care':   'border-l-4 border-l-sky-300',
  'Maintenance':  'border-l-4 border-l-yellow-300',
  'Conditioning': 'border-l-4 border-l-emerald-300',
  'Travel':       'border-l-4 border-l-indigo-300',
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmt12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

// Format scheduled care item name with dosage/serving_size (mirrors Care Log description format)
// Falls back to catalog lookup for items that don't have dosage stored directly
const fmtScheduledName = (item, catalog) => {
  const nameStr = item.name || '';
  const typeStr = item.type || '';
  const base = nameStr.toLowerCase().startsWith(typeStr.toLowerCase()) ? nameStr : typeStr ? `${typeStr} — ${nameStr}` : nameStr;

  let extra = '';
  if (item.dosage) {
    extra = ` (${item.dosage}${item.dosage_unit ? ' ' + item.dosage_unit : ''})`;
  } else if (item.serving_size != null) {
    extra = ` (${item.serving_size}${item.serving_size_units ? ' ' + item.serving_size_units : ''})`;
  } else if (catalog && item.care_catalog_item_id) {
    const cat = catalog.find(c => c.id === item.care_catalog_item_id);
    if (cat?.dosage) {
      extra = ` (${cat.dosage}${cat.dosage_unit ? ' ' + cat.dosage_unit : ''})`;
    } else if (cat?.serving_size != null) {
      extra = ` (${cat.serving_size}${cat.serving_size_units ? ' ' + cat.serving_size_units : ''})`;
    }
  }
  return base + extra;
};

// ── Horse Multi-Select ────────────────────────────────────────────────────────
function HorseMultiSelect({ horses, value, onChange, locations }) {
  const [search, setSearch] = useState('');
  const confirm = useConfirm();
  const [locFilter, setLocFilter] = useState('');
  const lower = search.toLowerCase();
  const filtered = horses.filter(h => {
    if (lower && !(h.name || h.system_name || '').toLowerCase().includes(lower)) return false;
    if (locFilter && h.farmfolio_location_id !== locFilter) return false;
    return true;
  });

  const QUICK_BUCKETS = [
    { key: 'all',      label: 'All',      fn: () => filtered.map(h => h.id) },
    { key: 'colts',    label: 'Colts',    fn: () => filtered.filter(h => h.gender === 'Colt').map(h => h.id) },
    { key: 'fillies',  label: 'Fillies',  fn: () => filtered.filter(h => h.gender === 'Filly').map(h => h.id) },
    { key: 'mares',    label: 'Mares',    fn: () => filtered.filter(h => h.gender === 'Mare').map(h => h.id) },
    { key: 'stallions',label: 'Stallions',fn: () => filtered.filter(h => h.gender === 'Horse' || h.gender === 'Stallion').map(h => h.id) },
    { key: 'geldings', label: 'Geldings', fn: () => filtered.filter(h => h.gender === 'Gelding' || h.gender === 'Ridgeling').map(h => h.id) },
    { key: 'yearlings',label: 'Yearlings',fn: () => filtered.filter(h => h.age === 'Yearling').map(h => h.id) },
    { key: 'weanlings',label: 'Weanlings',fn: () => filtered.filter(h => h.age === 'Weanling').map(h => h.id) },
    { key: 'broodmares', label: 'Broodmares', fn: () => filtered.filter(h => h.horse_type_female === 'broodmare').map(h => h.id) },
    { key: 'top',      label: 'Top Horses', fn: () => filtered.filter(h => h.is_top_horse).map(h => h.id) },
    { key: 'layup',    label: 'On Layup', fn: () => filtered.filter(h => h.on_layup).map(h => h.id) },
  ];

  const toggle = (id) => {
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
  };

  const applyBucket = (fn) => {
    const ids = fn();
    const newSet = new Set([...value, ...ids]);
    onChange([...newSet]);
  };

  return (
    <div className="space-y-2 border rounded p-2">
      <div className="flex items-center gap-1">
        <Search className="w-3.5 h-3.5 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search horses…" className="h-7 text-xs border-0 focus-visible:ring-0 flex-1" />
        {locations?.length > 0 && (
          <select
            className="h-7 text-[10px] border rounded px-1.5 bg-white text-gray-700 shrink-0"
            value={locFilter}
            onChange={e => setLocFilter(e.target.value)}
          >
            <option value="">All Locations</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
        {value.length > 0 && (
          <Button variant="ghost" size="sm" className="h-6 text-xs shrink-0"
            onClick={() => onChange([])}>Clear ({value.length})</Button>
        )}
      </div>
      <div className="flex flex-wrap gap-1">
        {QUICK_BUCKETS.map(b => (
          <button key={b.key} type="button" onClick={() => applyBucket(b.fn)}
            className="px-2 py-0.5 rounded-full text-[10px] font-medium border bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            data-testid={`horse-bucket-${b.key}`}>
            + {b.label}
          </button>
        ))}
      </div>
      <div className="max-h-48 overflow-y-auto space-y-1 border-t pt-2">
        {filtered.length === 0 && (
          <p className="text-xs text-muted-foreground italic">No horses</p>
        )}
        {filtered.map(h => (
          <label key={h.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 px-1 rounded">
            <input type="checkbox" checked={value.includes(h.id)} onChange={() => toggle(h.id)}
              data-testid={`horse-check-${h.id}`} />
            <span className="flex-1 truncate">{h.system_name || h.name}</span>
            <span className="text-[10px] text-muted-foreground">{h.gender} · {h.age}</span>
            {h.on_layup && <Badge className="bg-red-600 text-white text-[9px] px-1 py-0">LAYUP</Badge>}
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Bulk Quick Log Modal ──────────────────────────────────────────────────────
function BulkQuickLogModal({ open, onClose, onSaved, horses, catalog, locations }) {
  const today = new Date().toLocaleDateString('sv');
  const now = new Date().toTimeString().slice(0, 5);

  const initForm = () => ({
    horse_ids: [],
    category: '',
    type: '',
    care_catalog_item_id: '',
    name: '',
    dosage: '', dosage_unit: '', units: '',
    log_date: today, log_time: now,
    location_id: '',
    performed_by: '', performed_by_type: 'In-house',
    comments: '',
    publish_to_owner_portal: false,
    billable: false, cost: '', cost_currency: 'USD',
  });

  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initForm()); /* eslint-disable-next-line */ }, [open]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const categories = useMemo(() => [...new Set(catalog.map(c => c.category))].filter(Boolean).sort(), [catalog]);
  const types      = useMemo(() => [...new Set(catalog.filter(c => c.category === form.category).map(c => c.type))].filter(Boolean).sort(), [catalog, form.category]);
  const items      = catalog.filter(c => c.category === form.category && (!form.type || c.type === form.type));

  const handleNameSelect = (id) => {
    if (id === '__none__') return f('care_catalog_item_id', '');
    const item = items.find(c => c.id === id);
    if (item) setForm(p => ({
      ...p, care_catalog_item_id: id, name: item.name,
      dosage: item.dosage || '', dosage_unit: item.dosage_unit || '',
      units: item.units ?? (item.serving_size ?? ''),
      billable: item.billable || false,
      cost: item.cost ?? '',
      cost_currency: item.cost_currency || p.cost_currency || 'USD',
    }));
  };

  const handleSave = async () => {
    if (!form.horse_ids.length) { toast.error('Select at least one horse'); return; }
    if (!form.category || !form.type || !form.name) { toast.error('Category, Type and Name are required'); return; }
    setSaving(true);
    try {
      await api.post('/farmfolio/global/bulk/care-log', {
        horse_ids: form.horse_ids,
        payload: {
          ...form,
          units: form.units !== '' ? parseFloat(form.units) : null,
          cost: form.billable && form.cost !== '' ? parseFloat(form.cost) || null : null,
          location_name: locations.find(l => l.id === form.location_id)?.name || '',
        },
      });
      toast.success(`Logged care for ${form.horse_ids.length} horse(s)`);
      onSaved();
      onClose();
    } catch { toast.error('Failed to save bulk care log'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Quick Log Care (Bulk)</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="font-semibold">Horses</Label>
            <HorseMultiSelect horses={horses} value={form.horse_ids} onChange={v => f('horse_ids', v)} locations={locations} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={form.category || '__none__'} onValueChange={v => f('category', v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Category…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Select —</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Type</Label>
              <Select value={form.type || '__none__'} onValueChange={v => f('type', v === '__none__' ? '' : v)} disabled={!form.category}>
                <SelectTrigger><SelectValue placeholder="Type…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Select —</SelectItem>
                  {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Name (from Care Catalog)</Label>
            <Select value={form.care_catalog_item_id || '__none__'} onValueChange={handleNameSelect} disabled={!form.type}>
              <SelectTrigger><SelectValue placeholder={form.type ? 'Select…' : 'Select type first'} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Select —</SelectItem>
                {items.map(i => <SelectItem key={i.id} value={i.id}>{i.name}{i.dosage ? ` (${i.dosage} ${i.dosage_unit || ''})` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Date</Label>
              <Input type="date" value={form.log_date} onChange={e => f('log_date', e.target.value)} className="time-filled" />
            </div>
            <div className="space-y-1">
              <Label>Time</Label>
              <Input type="time" value={form.log_time} onChange={e => f('log_time', e.target.value)}
                className={form.log_time ? 'time-filled' : ''} />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Location</Label>
            <Select value={form.location_id || '__none__'} onValueChange={v => f('location_id', v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Performed By</Label>
              <Input value={form.performed_by} onChange={e => f('performed_by', e.target.value)} placeholder="Name" />
            </div>
            <div className="space-y-1">
              <Label>Type</Label>
              <Select value={form.performed_by_type} onValueChange={v => f('performed_by_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['In-house','Vet','Farrier','Trainer','Other'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Comments</Label>
            <Textarea rows={2} value={form.comments} onChange={e => f('comments', e.target.value)} />
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={form.publish_to_owner_portal} onCheckedChange={v => f('publish_to_owner_portal', v)} />
            <Label className="text-sm cursor-pointer">Publish to Owner Portal</Label>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="bulk-quicklog-save"
            className="bg-primary hover:bg-primary/90 text-white">
            {saving ? 'Saving…' : `Log for ${form.horse_ids.length || 0} horse(s)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Bulk Complete Modal — with per-horse "By" override ───────────────────────
function BulkCompleteModal({ open, onClose, onSaved, items, horsesById }) {
  const today = new Date().toLocaleDateString('sv');
  const now = new Date().toTimeString().slice(0, 5);

  const [defByName, setDefByName] = useState('');
  const [defByType, setDefByType] = useState('In-house');
  const [logDate, setLogDate]     = useState(today);
  const [logTime, setLogTime]     = useState(now);
  const [comments, setComments]   = useState('');
  const [publish, setPublish]     = useState(false);
  const [overrides, setOverrides] = useState({}); // { scheduled_care_id: { performed_by, performed_by_type } }
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    if (open) {
      setDefByName(''); setDefByType('In-house');
      setLogDate(today); setLogTime(now); setComments(''); setPublish(false);
      setOverrides({});
    }
    // eslint-disable-next-line
  }, [open]);

  const setOverride = (id, key, val) => {
    setOverrides(p => ({ ...p, [id]: { ...(p[id] || {}), [key]: val } }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const itemsPayload = items.map(it => ({
        scheduled_care_id: it.id,
        performed_by:      overrides[it.id]?.performed_by ?? '',
        performed_by_type: overrides[it.id]?.performed_by_type ?? '',
      }));
      await api.post('/farmfolio/global/bulk/complete', {
        items: itemsPayload,
        defaults: {
          log_date: logDate,
          log_time: logTime,
          performed_by: defByName,
          performed_by_type: defByType,
          comments,
          publish_to_owner_portal: publish,
        },
      });
      toast.success(`Completed ${items.length} item(s)`);
      onSaved();
      onClose();
    } catch { toast.error('Failed to complete'); }
    setSaving(false);
  };

  const soldItems = items.filter(it => {
    const h = horsesById[it.horse_id];
    return h?.sale_status === 'Sold' && !h?.keep_in_care;
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Bulk Complete — {items.length} item(s)</DialogTitle></DialogHeader>
        {soldItems.length > 0 && (
          <div className="flex items-start justify-between gap-3 px-1 py-2 rounded bg-amber-50 border border-amber-200 text-sm text-amber-800">
            <span><strong>{soldItems.length} item(s)</strong> belong to sold horses — completing them will not schedule future occurrences.</span>
            <button
              className="shrink-0 text-xs font-medium text-primary hover:underline whitespace-nowrap"
              onClick={async () => {
                const soldHorseIds = [...new Set(soldItems.map(it => it.horse_id))];
                await Promise.all(soldHorseIds.map(id => api.put(`/horses/${id}`, { keep_in_care: true })));
                onSaved();
                onClose();
              }}
            >
              Enable Keep in Care
            </button>
          </div>
        )}
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Default Performed By</Label>
              <Input value={defByName} onChange={e => setDefByName(e.target.value)} placeholder="Name" />
            </div>
            <div className="space-y-1">
              <Label>Type</Label>
              <Select value={defByType} onValueChange={setDefByType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['In-house','Vet','Farrier','Trainer','Other'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Date</Label>
              <Input type="date" value={logDate} onChange={e => setLogDate(e.target.value)} className="time-filled" />
            </div>
            <div className="space-y-1">
              <Label>Time</Label>
              <Input type="time" value={logTime} onChange={e => setLogTime(e.target.value)} className={logTime ? 'time-filled' : ''} />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Comments (applied to all)</Label>
            <Textarea rows={2} value={comments} onChange={e => setComments(e.target.value)} />
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={publish} onCheckedChange={setPublish} />
            <Label className="text-sm cursor-pointer">Publish to Owner Portal</Label>
          </div>

          <div className="border-t pt-2">
            <Label className="text-sm font-semibold">Per-horse "By" overrides (optional)</Label>
            <p className="text-xs text-muted-foreground mb-2">Blank fields use the default above.</p>
            <div className="space-y-1 max-h-56 overflow-y-auto">
              {items.map(it => {
                const horse = horsesById[it.horse_id] || {};
                return (
                  <div key={it.id} className="grid grid-cols-[1fr_1fr_120px] gap-2 items-center text-xs">
                    <div className="truncate">
                      <span className="font-medium">{horse.system_name || horse.name || it.horse_id}</span>
                      <span className="text-muted-foreground"> · {it.type} — {it.name}</span>
                    </div>
                    <Input className="h-7 text-xs" placeholder={defByName || 'By…'}
                      value={overrides[it.id]?.performed_by || ''}
                      onChange={e => setOverride(it.id, 'performed_by', e.target.value)} />
                    <Select value={overrides[it.id]?.performed_by_type || ''} onValueChange={v => setOverride(it.id, 'performed_by_type', v)}>
                      <SelectTrigger className="h-7 text-xs"><SelectValue placeholder={defByType} /></SelectTrigger>
                      <SelectContent>
                        {['In-house','Vet','Farrier','Trainer','Other'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="bulk-complete-save"
            className="bg-primary hover:bg-primary/90 text-white">
            {saving ? 'Completing…' : 'Complete All'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Global Bulk Schedule Modal ────────────────────────────────────────────────
function GlobalBulkScheduleModal({ open, onClose, onSaved, horses, catalog, locations }) {
  const today = new Date().toLocaleDateString('sv');
  const nowTime = new Date().toTimeString().slice(0, 5);

  const initForm = () => ({
    horse_ids: [],
    category: '', type: '', care_catalog_item_id: '',
    name: '', dosage: '', dosage_unit: '', units: '',
    start_date: today, preferred_time: nowTime, end_date: '',
    location_id: '',
    is_routine: false,
    frequency_type: 'daily',
    frequency_config: { ...FREQ_DEFAULTS.daily },
    billable: false, cost: '', cost_currency: 'USD',
    notes: '',
  });

  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initForm()); /* eslint-disable-next-line */ }, [open]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const categories = useMemo(() => [...new Set(catalog.map(c => c.category))].filter(Boolean).sort(), [catalog]);
  const types = useMemo(() => [...new Set(catalog.filter(c => c.category === form.category).map(c => c.type))].filter(Boolean).sort(), [catalog, form.category]);
  const items = catalog.filter(c => c.category === form.category && (!form.type || c.type === form.type));

  const handleNameSelect = (id) => {
    if (id === '__none__') { f('care_catalog_item_id', ''); return; }
    const item = items.find(c => c.id === id);
    if (item) {
      const ft = item.frequency_type || 'daily';
      setForm(p => ({
        ...p,
        care_catalog_item_id: id, name: item.name,
        dosage: item.dosage || '', dosage_unit: item.dosage_unit || '',
        units: item.units ?? (item.serving_size ?? ''),
        is_routine: item.routine || p.is_routine,
        frequency_type: ft,
        frequency_config: { ...(FREQ_DEFAULTS[ft] || {}), ...(item.frequency_config || {}) },
        billable: item.billable || false,
        cost: item.cost ?? '', cost_currency: item.cost_currency || p.cost_currency || 'USD',
      }));
    }
  };

  const handleSave = async () => {
    if (!form.horse_ids.length) { toast.error('Select at least one horse'); return; }
    if (!form.category) { toast.error('Category is required'); return; }
    if (!form.type) { toast.error('Type is required'); return; }
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.type === 'Turnout' && form.horse_ids.length > 1) {
      toast.error('Turnout can only be scheduled for one horse at a time'); return;
    }
    setSaving(true);
    try {
      await api.post('/farmfolio/global/bulk/scheduled-care', {
        horse_ids: form.horse_ids,
        payload: {
          ...form,
          units: form.units !== '' ? parseFloat(form.units) : null,
          cost: form.billable && form.cost !== '' ? parseFloat(form.cost) || null : null,
          end_date: form.end_date || null,
          location_name: locations.find(l => l.id === form.location_id)?.name || '',
          frequency_type: form.is_routine ? form.frequency_type : 'once',
          frequency_config: form.is_routine ? (form.frequency_config || {}) : {},
        },
      });
      toast.success(`Scheduled care for ${form.horse_ids.length} horse(s)`);
      onSaved(); onClose();
    } catch { toast.error('Failed to schedule care'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Schedule Care (Bulk)</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="font-semibold">Horses</Label>
            <HorseMultiSelect horses={horses} value={form.horse_ids} onChange={v => f('horse_ids', v)} locations={locations} />
          </div>
          <div className="space-y-1">
            <Label className="font-semibold">Category</Label>
            <Select value={form.category || '__none__'} onValueChange={v => {
              const val = v === '__none__' ? '' : v;
              setForm(p => ({ ...p, category: val, type: '', care_catalog_item_id: '', name: '', dosage: '', dosage_unit: '', units: '' }));
            }}>
              <SelectTrigger><SelectValue placeholder="Select category…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Select —</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="font-semibold">Type</Label>
            <Select value={form.type || '__none__'} onValueChange={v => {
              const val = v === '__none__' ? '' : v;
              setForm(p => ({ ...p, type: val, care_catalog_item_id: '', name: '', dosage: '', dosage_unit: '', units: '' }));
            }} disabled={!form.category}>
              <SelectTrigger><SelectValue placeholder={form.category ? 'Select type…' : 'Select category first'} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Select —</SelectItem>
                {types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="font-semibold">Name</Label>
            <Select value={form.care_catalog_item_id || '__none__'} onValueChange={handleNameSelect} disabled={!form.type}>
              <SelectTrigger><SelectValue placeholder={form.type ? 'Select from catalog…' : 'Select type first'} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Select —</SelectItem>
                {items.map(i => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.name}{i.dosage ? ` (${i.dosage} ${i.dosage_unit || ''})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="font-semibold">Date</Label>
              <Input type="date" value={form.start_date} onChange={e => f('start_date', e.target.value)} className="time-filled" />
            </div>
            <div className="space-y-1">
              <Label className="font-semibold">Time</Label>
              <Input type="time" value={form.preferred_time} onChange={e => f('preferred_time', e.target.value)}
                className={form.preferred_time ? 'time-filled' : ''} />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="font-semibold">Location</Label>
            <Select value={form.location_id || '__none__'} onValueChange={v => f('location_id', v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="font-semibold">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Optional notes" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.is_routine} onCheckedChange={v => f('is_routine', v)} />
            <Label className="cursor-pointer">Routine item</Label>
          </div>
          {form.is_routine && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Frequency</Label>
                <Select value={form.frequency_type}
                  onValueChange={v => setForm(p => ({ ...p, frequency_type: v, frequency_config: { ...FREQ_DEFAULTS[v] } }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQ_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="p-2 border rounded bg-muted/20">
                <FrequencyConfig form={form} setForm={setForm} />
              </div>
              <div className="space-y-1">
                <Label className="font-semibold">End Date</Label>
                <Input type="date" value={form.end_date} onChange={e => f('end_date', e.target.value)}
                  className={form.end_date ? 'time-filled' : ''} />
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Switch checked={form.billable} onCheckedChange={v => f('billable', v)} />
            <Label className="cursor-pointer">Billable</Label>
          </div>
          {form.billable && (
            <div className="space-y-1">
              <Label>Cost per unit</Label>
              <div className="flex gap-2">
                <Select value={form.cost_currency || 'USD'} onValueChange={v => f('cost_currency', v)}>
                  <SelectTrigger className="w-20 shrink-0"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" min="0" step="0.01" value={form.cost}
                  onChange={e => f('cost', e.target.value)} placeholder="0.00" className="flex-1" />
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="bulk-schedule-save"
            className="bg-primary hover:bg-primary/90 text-white">
            {saving ? 'Saving…' : `Schedule for ${form.horse_ids.length || 0} horse(s)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Global Page ─────────────────────────────────────────────────────────
export default function FarmFolioGlobalPage() {
  const { user: currentUser, isFarm } = useAuth();
  const isFarmMgr = currentUser?.roles?.includes('tenant_farm_manager');

  // Gate: redirect non-farm tenants away
  const navigate = useNavigate();
  useEffect(() => {
    if (isFarm === false) {
      navigate('/dashboard', { replace: true });
    }
  }, [isFarm, navigate]);

  const qc = useQueryClient();

  // ── UI state — declared FIRST so lazy-query `enabled` flags can reference them ──
  // Manage Scheduled Care panel
  const [showManageScheduled, setShowManageScheduled] = useState(false);
  // Layup list dialog
  const [showLayupList, setShowLayupList]   = useState(false);

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: globalData, isLoading: loading } = useQuery({
    queryKey: QK.farmfolioGlobal(),
    queryFn: fetchFarmfolioGlobal(),
    onError: (e) => {
      if (e?.response?.status === 403) toast.error('You are not authorized to view FarmFolio');
      else toast.error('Failed to load FarmFolio');
    },
  });
  const horses    = globalData?.horses    ?? [];
  const dueToday  = globalData?.dueToday  ?? [];
  const upcoming  = globalData?.upcoming  ?? [];
  const careLog   = globalData?.careLog   ?? [];
  const catalog   = globalData?.catalog   ?? [];
  const locations = globalData?.locations ?? [];

  const { data: scheduledAll = [], isLoading: loadingScheduled, refetch: refetchScheduled } = useQuery({
    queryKey: QK.farmfolioGlobalScheduled(),
    queryFn: fetchFarmfolioGlobalScheduled(),
    enabled: showManageScheduled,
    onError: () => toast.error('Failed to load scheduled care'),
  });

  const { data: layupListData = [], isLoading: loadingLayups, refetch: refetchLayups } = useQuery({
    queryKey: QK.farmfolioLayups(),
    queryFn: fetchFarmfolioLayups(),
    enabled: showLayupList,
  });
  const layupList = layupListData;

  const invalidateGlobal    = () => qc.invalidateQueries({ queryKey: QK.farmfolioGlobal() });
  const invalidateScheduled = () => qc.invalidateQueries({ queryKey: QK.farmfolioGlobalScheduled() });
  const invalidateLayups    = () => qc.invalidateQueries({ queryKey: QK.farmfolioLayups() });

  // ── Remaining UI state ────────────────────────────────────────────────────
  const [showCatalog, setShowCatalog]   = useState(false);
  const [quickLog, setQuickLog]         = useState({ open: false, prefill: null });
  const [turnoutOpen, setTurnoutOpen]   = useState(false);
  const [travelOpen, setTravelOpen]     = useState(false);
  const [statsOpen, setStatsOpen]       = useState(false);
  const [noteOpen, setNoteOpen]         = useState(false);
  const [layupOpen, setLayupOpen]       = useState(false);
  const [bulkComplete, setBulkComplete] = useState({ open: false, items: [] });

  // Selection state for Today / Upcoming
  const [selectedDue, setSelectedDue]   = useState(new Set());
  const [selectedUpc, setSelectedUpc]   = useState(new Set());

  // Manage Scheduled Care panel (remaining state)
  const [selectedScheduled, setSelectedScheduled]     = useState(new Set());
  const [bulkScheduleOpen, setBulkScheduleOpen]       = useState(false);
  const [scheduleEditModal, setScheduleEditModal]     = useState({ open: false, editing: null });
  const [invoiceModalOpen, setInvoiceModalOpen]       = useState(false);
  const [careLogDetail, setCareLogDetail]             = useState(null);

  // Manage Scheduled Care filters
  const [schedCategoryFilter, setSchedCategoryFilter] = useState('All');
  const [schedSearch, setSchedSearch]                 = useState('');
  const [schedBreedingFilter, setSchedBreedingFilter] = useState(false);
  const [bulkEditOpen, setBulkEditOpen]               = useState(false);

  // Filter for Today/Upcoming sections
  const [filterHorse, setFilterHorse]       = useState('');
  const [filterLocation, setFilterLocation] = useState('');

  // Layup list additional state
  const [layupOnly, setLayupOnly]           = useState(false);

  const activeLayupCount = horses.filter(h => h.on_layup).length;
  const horsesById = useMemo(() => Object.fromEntries(horses.map(h => [h.id, h])), [horses]);

  const openLayupList = () => setShowLayupList(true);

  // Horse IDs matching location filter
  const locationFilteredHorseIds = useMemo(() => {
    if (!filterLocation) return null;
    return new Set(horses.filter(h => h.farmfolio_location_id === filterLocation).map(h => h.id));
  }, [filterLocation, horses]);

  const filteredDue = dueToday.filter(i => {
    if (filterHorse && i.horse_id !== filterHorse) return false;
    if (locationFilteredHorseIds && !locationFilteredHorseIds.has(i.horse_id)) return false;
    return true;
  });
  const filteredUpc = upcoming.filter(i => {
    if (filterHorse && i.horse_id !== filterHorse) return false;
    if (locationFilteredHorseIds && !locationFilteredHorseIds.has(i.horse_id)) return false;
    return true;
  });

  const toggleSel = (set, setter, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  const startBulkComplete = (sourceSet, sourceItems) => {
    const items = sourceItems.filter(i => sourceSet.has(i.id));
    if (!items.length) return;
    setBulkComplete({ open: true, items });
  };

  const handleBulkPublish = async (ids, publish) => {
    try {
      await api.put('/farmfolio/global/bulk/care-log/publish', { ids, publish });
      toast.success(`${publish ? 'Published' : 'Unpublished'} ${ids.length} entry(ies)`);
      invalidateGlobal();
    } catch { toast.error('Failed to update'); }
  };

  const handleDeleteSelectedScheduled = async () => {
    if (!selectedScheduled.size) return;
    try {
      await api.delete('/farmfolio/global/bulk/scheduled-care', {
        data: { ids: [...selectedScheduled] },
      });
      toast.success(`Deleted ${selectedScheduled.size} item(s)`);
      setSelectedScheduled(new Set());
      invalidateScheduled();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDeleteScheduledItem = async (item) => {
    if (!await confirm({ title: `Remove "${item.name}" from schedule?`, destructive: true })) return;
    try {
      await api.delete(`/farmfolio/scheduled-care/${item.id}`);
      toast.success('Removed from schedule');
      setSelectedScheduled(prev => { const n = new Set(prev); n.delete(item.id); return n; });
      invalidateScheduled();
    } catch { toast.error('Failed to remove'); }
  };

  const renderCareRow = (item, selected, onToggle) => (
    <div key={item.id}
      className={`flex items-center gap-2 p-2.5 border rounded hover:bg-muted/20 ${CARE_BORDER[item.category] || 'border-l-4 border-l-gray-300'}`}>
      <input type="checkbox" checked={selected} onChange={() => onToggle(item.id)}
        data-testid={`global-care-check-${item.id}`} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium break-words">
          {item.horse_name}{item.horse_on_layup && <Badge className="ml-1.5 bg-red-600 text-white text-[10px] px-1 py-0">LAYUP</Badge>}
        </div>
        <p className="text-xs text-muted-foreground break-words">
          {fmtScheduledName(item, catalog)}
          {item.next_due_date && ` · ${formatDate(item.next_due_date)}`}
          {item.preferred_time && ` @ ${fmt12(item.preferred_time)}`}
          {item.location_name && ` · ${item.location_name}`}
        </p>
      </div>
    </div>
  );

  if (showManageScheduled) {
    const SCHED_CATS = ['All', 'Medical', 'Daily Care', 'Maintenance', 'Conditioning'];
    const SCHED_COLORS = {
      'All':          { active: 'bg-primary text-white border-primary',           inactive: 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50' },
      'Medical':      { active: 'bg-purple-100 text-purple-700 border-purple-300',     inactive: 'bg-purple-50 text-purple-500 border-purple-200 hover:bg-purple-100' },
      'Daily Care':   { active: 'bg-sky-100 text-sky-700 border-sky-300',              inactive: 'bg-sky-50 text-sky-500 border-sky-200 hover:bg-sky-100' },
      'Maintenance':  { active: 'bg-yellow-100 text-yellow-700 border-yellow-300',     inactive: 'bg-yellow-50 text-yellow-600 border-yellow-200 hover:bg-yellow-100' },
      'Conditioning': { active: 'bg-emerald-100 text-emerald-700 border-emerald-300',  inactive: 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100' },
    };
    const localToday = new Date().toLocaleDateString('sv');
    const breedingCatalogIds = new Set((catalog || []).filter(c => c.is_breeding).map(c => c.id));
    const filteredScheduledAll = scheduledAll.filter(i => {
      const matchCat = schedCategoryFilter === 'All' || i.category === schedCategoryFilter;
      const matchQ   = !schedSearch || (i.name + ' ' + i.type + ' ' + (i.horse_name || '')).toLowerCase().includes(schedSearch.toLowerCase());
      const matchBreed = !schedBreedingFilter || (i.care_catalog_item_id ? breedingCatalogIds.has(i.care_catalog_item_id) : !!i.is_breeding);
      return matchCat && matchQ && matchBreed;
    });
    const todayItems     = filteredScheduledAll.filter(i => i.next_due_date && i.next_due_date <= localToday);
    const upcomingItems  = filteredScheduledAll.filter(i => !i.next_due_date || i.next_due_date > localToday);

    // Bulk edit eligibility
    const selItems    = filteredScheduledAll.filter(i => selectedScheduled.has(i.id));
    const canBulkEdit = selItems.length > 1 &&
      selItems.every(i => i.category === selItems[0].category && i.type === selItems[0].type && i.name === selItems[0].name);

    const toggleSelScheduled = (id) => {
      const next = new Set(selectedScheduled);
      if (next.has(id)) next.delete(id); else next.add(id);
      setSelectedScheduled(next);
    };

    const renderScheduledRow = (item) => (
      <div key={item.id}
        className={`flex items-center gap-2 p-2.5 border rounded hover:bg-muted/20 ${CARE_BORDER[item.category] || 'border-l-4 border-l-gray-300'}`}>
        <input type="checkbox" checked={selectedScheduled.has(item.id)}
          onChange={() => toggleSelScheduled(item.id)}
          data-testid={`global-sched-check-${item.id}`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">
            <span className="text-primary font-semibold">{item.horse_name}</span>
            {item.horse_on_layup && <Badge className="ml-1.5 bg-red-600 text-white text-[10px] px-1 py-0">LAYUP</Badge>}
            <span className="text-muted-foreground font-normal"> · {fmtScheduledName(item, catalog)}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {item.next_due_date ? formatDate(item.next_due_date) : <span className="capitalize">{(item.frequency_type || 'once').replace('_', ' ')}</span>}
            {item.preferred_time && ` @ ${fmt12(item.preferred_time)}`}
            {item.location_name && ` · ${item.location_name}`}
          </p>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7"
            onClick={() => setScheduleEditModal({ open: true, editing: item })}
            title="Edit"
            data-testid={`global-sched-edit-${item.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7"
            onClick={() => handleDeleteScheduledItem(item)}
            title="Delete"
            data-testid={`global-sched-delete-${item.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    );

    const handleExportScheduled = () => {
      const rows = [
        ['Horse', 'Category', 'Type', 'Name', 'Frequency', 'Next Due', 'Time', 'Location', 'Routine'],
        ...scheduledAll.map(item => [
          item.horse_name || '',
          item.category || '',
          item.type || '',
          item.name || '',
          (item.frequency_type || 'once').replace('_', ' '),
          item.next_due_date || '',
          item.preferred_time || '',
          item.location_name || '',
          item.is_routine ? 'Yes' : 'No',
        ]),
      ];
      const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'scheduled_care_all_horses.csv'; a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <SidebarLayout>
        <div className="space-y-6" data-testid="farmfolio-manage-scheduled-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h1 className="font-serif text-3xl font-semibold">FarmFolio</h1>
              <p className="text-muted-foreground mt-1">Manage Scheduled Care — All Horses</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="h-8 text-xs bg-primary hover:bg-primary/90 text-white"
                onClick={() => setBulkScheduleOpen(true)} data-testid="global-sched-add">
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs"
                onClick={() => setLayupOpen(true)} data-testid="global-sched-layup">
                + Layup
              </Button>
              <Button size="sm" variant="outline" className="h-8 text-xs"
                onClick={async () => { setShowManageScheduled(false); setSelectedScheduled(new Set()); }}
                data-testid="global-sched-close">
                Close
              </Button>
            </div>
          </div>
          {scheduledAll.length > 0 && (
            <div className="flex justify-end -mt-4">
              <Button size="sm" variant="outline" className="h-7 text-xs"
                onClick={handleExportScheduled} data-testid="global-sched-export">
                <Download className="w-3 h-3 mr-1" /> Export CSV
              </Button>
            </div>
          )}

          {selectedScheduled.size > 0 && (
            <div className="flex items-center gap-3 p-2.5 bg-destructive/10 border border-destructive/20 rounded">
              <span className="text-sm font-medium text-destructive">{selectedScheduled.size} selected</span>
              {canBulkEdit && (
                <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => setBulkEditOpen(true)}>Bulk Edit</Button>
              )}
              <Button size="sm" variant="destructive" className="h-7 text-xs"
                onClick={handleDeleteSelectedScheduled} data-testid="global-sched-delete-selected">
                <Trash2 className="w-3 h-3 mr-1" /> Delete Selected
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs"
                onClick={() => setSelectedScheduled(new Set())}>Clear</Button>
            </div>
          )}

          <Card>
            <CardContent className="pt-4">
              {/* Category filter pills + text search */}
              <div className="flex flex-wrap gap-2 items-center mb-4">
                <div className="flex flex-wrap gap-1">
                  {SCHED_CATS.map(c => (
                    <button key={c} type="button" onClick={() => setSchedCategoryFilter(c)}
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                        schedCategoryFilter === c
                          ? (SCHED_COLORS[c]?.active  || SCHED_COLORS['All'].active)
                          : (SCHED_COLORS[c]?.inactive || SCHED_COLORS['All'].inactive)
                      }`}>{c}</button>
                  ))}
                  <button type="button" onClick={() => setSchedBreedingFilter(v => !v)}
                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                      schedBreedingFilter
                        ? 'bg-pink-100 text-pink-700 border-pink-300'
                        : 'bg-pink-50 text-pink-400 border-pink-200 hover:bg-pink-100'
                    }`}>Breeding</button>
                </div>
                <div className="relative flex-1 min-w-[140px]">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                  <Input value={schedSearch} onChange={e => setSchedSearch(e.target.value)}
                    placeholder="Search…" className="h-7 text-xs pl-7" />
                </div>
              </div>

              {loadingScheduled ? (
                <p className="text-sm text-center py-8 text-muted-foreground">Loading…</p>
              ) : scheduledAll.length === 0 ? (
                <p className="text-sm text-center py-8 text-muted-foreground italic">No scheduled care. Click "Add" to schedule care across horses.</p>
              ) : (
                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-semibold mb-2">Today's Care</p>
                    {todayItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic py-2 text-center">No care due today.</p>
                    ) : (
                      <div className="space-y-2">{todayItems.map(renderScheduledRow)}</div>
                    )}
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-sm font-semibold">Upcoming Care</p>
                      {upcomingItems.length > 0 && <Badge className="bg-primary/10 text-primary text-xs">{upcomingItems.length}</Badge>}
                    </div>
                    {upcomingItems.length === 0 ? (
                      <p className="text-sm text-muted-foreground italic py-2 text-center">No upcoming care scheduled.</p>
                    ) : (
                      <div className="space-y-2">{upcomingItems.map(renderScheduledRow)}</div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <GlobalBulkScheduleModal
            open={bulkScheduleOpen}
            onClose={() => setBulkScheduleOpen(false)}
            onSaved={() => { invalidateScheduled(); invalidateGlobal(); setBulkScheduleOpen(false); }}
            horses={horses}
            catalog={catalog}
            locations={locations}
          />

          <ScheduleCareModal
            open={scheduleEditModal.open}
            onClose={() => setScheduleEditModal({ open: false, editing: null })}
            onSaved={() => { invalidateScheduled(); invalidateGlobal(); setScheduleEditModal({ open: false, editing: null }); }}
            horse={scheduleEditModal.editing
              ? { id: scheduleEditModal.editing.horse_id, name: scheduleEditModal.editing.horse_name, system_name: scheduleEditModal.editing.horse_name }
              : null}
            locations={locations}
            catalogItems={catalog}
            editing={scheduleEditModal.editing}
          />

          <LayupModal
            open={layupOpen}
            onClose={() => setLayupOpen(false)}
            onSaved={invalidateGlobal}
            catalogItems={catalog}
            availableHorses={horses}
          />

          <BulkEditScheduledModal
            open={bulkEditOpen}
            onClose={() => setBulkEditOpen(false)}
            onSaved={() => { setSelectedScheduled(new Set()); invalidateScheduled(); setBulkEditOpen(false); }}
            items={scheduledAll.filter(i => selectedScheduled.has(i.id))}
            locations={locations}
            catalogItems={catalog}
          />
        </div>
      </SidebarLayout>
    );
  }

  if (showCatalog) {
    return (
      <SidebarLayout>
        <div className="space-y-6">
          <CareCatalogPanel onClose={() => setShowCatalog(false)} onSaved={invalidateGlobal} />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
    <div className="space-y-6" data-testid="farmfolio-global-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-serif text-3xl font-semibold">FarmFolio</h1>
          <p className="text-muted-foreground mt-1">
            Farm-wide care management across all horses
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isFarmMgr && (
          <Button size="sm" variant="outline" className="h-8 text-xs"
            onClick={() => setInvoiceModalOpen(true)} data-testid="global-generate-invoice-btn">
            <FileText className="w-3 h-3 mr-1" /> Generate Invoice
          </Button>
          )}
          <Button size="sm" variant="outline" className="h-8 text-xs"
            onClick={() => setShowManageScheduled(true)} data-testid="global-manage-scheduled">
            <Calendar className="w-3 h-3 mr-1" /> Manage Scheduled Care
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs"
            onClick={() => setShowCatalog(true)} data-testid="global-care-catalog">
            <BookOpen className="w-3 h-3 mr-1" /> Care Catalog
          </Button>
        </div>
      </div>

      {/* Quick Log — desktop: single grouped row; mobile: 3-per-row grid below Today's Care */}
      <Card className="hidden sm:block">
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Quick Log</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {(() => {
            const open = (item) => {
              if (item.isLayup) { setLayupOpen(true); return; }
              if (item.key === 'turnout') { setTurnoutOpen(true); return; }
              if (item.key === 'travel')  { setTravelOpen(true); return; }
              if (item.key === 'stats')   { setStatsOpen(true); return; }
              if (item.key === 'note')    { setNoteOpen(true); return; }
              setQuickLog({ open: true, prefill: item.key === 'other' ? null : { category: item.category, type: item.type } });
            };
            const qlBtn = (item) => {
              const Icon = item.icon; const Icon2 = item.icon2;
              return (
                <button key={item.key} onClick={() => open(item)}
                  data-testid={`global-ql-${item.key}`}
                  className="flex flex-col items-center justify-center gap-1 p-2 bg-white rounded-xl hover:shadow-md transition-shadow flex-1 min-w-0">
                  {Icon2 ? (
                    <div className="flex items-center gap-0.5">
                      <Icon className="w-5 h-5 text-primary" /><Icon2 className="w-5 h-5 text-primary" />
                    </div>
                  ) : <Icon className="w-5 h-5 text-primary" />}
                  <span className="text-[10px] font-bold text-primary text-center leading-tight">{item.label}</span>
                </button>
              );
            };
            const plainBtn = (item) => {
              const Icon = item.icon;
              return (
                <button key={item.key} onClick={() => open(item)}
                  data-testid={`global-ql-${item.key}`}
                  className="flex flex-col items-center justify-center gap-1 p-2 bg-white border rounded-xl hover:shadow-md transition-shadow flex-1 min-w-0">
                  <Icon className="w-5 h-5 text-primary" />
                  <span className="text-[10px] font-bold text-primary text-center leading-tight">{item.label}</span>
                </button>
              );
            };
            return (
              <div className="flex flex-nowrap items-stretch gap-2 w-full">
                <div className="bg-purple-100 rounded-2xl p-1.5 flex gap-1.5" style={{ flex: 3 }}>{QL_MEDICAL.map(qlBtn)}</div>
                <div className="bg-sky-100 rounded-2xl p-1.5 flex gap-1.5" style={{ flex: 3 }}>{QL_DAILY.map(qlBtn)}</div>
                <div className="bg-yellow-100 rounded-2xl p-1.5 flex gap-1.5" style={{ flex: 2 }}>{QL_MAINTENANCE.map(qlBtn)}</div>
                <div className="bg-emerald-100 rounded-2xl p-1.5 flex gap-1.5" style={{ flex: 1 }}>{qlBtn(QL_CONDITIONING)}</div>
                <div className="flex gap-1.5" style={{ flex: 3 }}>{QL_MISC.map(plainBtn)}</div>
                <div className="flex gap-1.5" style={{ flex: 1 }}>{plainBtn({ key: 'note', label: 'Add Note', icon: NotepadText })}</div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {activeLayupCount > 0 && (
        <div className="flex items-center gap-3 p-3 border border-red-200 bg-red-50 rounded"
          data-testid="global-layup-banner">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
          <p className="text-sm text-red-700">
            <span className="font-semibold">{activeLayupCount}</span> horse{activeLayupCount > 1 ? 's are' : ' is'} on active layup.
          </p>
          <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 ml-auto"
            onClick={openLayupList} data-testid="global-layup-view-all">View All</Button>
        </div>
      )}

      {/* Today's Care + Upcoming */}
      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <select
          className="h-8 text-xs border rounded px-2 bg-white text-gray-700"
          value={filterHorse}
          onChange={e => setFilterHorse(e.target.value)}
          data-testid="global-filter-horse"
        >
          <option value="">All Horses</option>
          {horses.map(h => (
            <option key={h.id} value={h.id}>{h.system_name || h.name}</option>
          ))}
        </select>
        {locations.length > 0 && (
          <select
            className="h-8 text-xs border rounded px-2 bg-white text-gray-700"
            value={filterLocation}
            onChange={e => setFilterLocation(e.target.value)}
            data-testid="global-filter-location"
          >
            <option value="">All Locations</option>
            {locations.map(loc => (
              <option key={loc.id} value={loc.id}>{loc.name}</option>
            ))}
          </select>
        )}
        {(filterHorse || filterLocation) && (
          <button
            className="h-8 text-xs text-muted-foreground hover:text-foreground underline"
            onClick={async () => { setFilterHorse(''); setFilterLocation(''); }}
          >Clear filters</button>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="py-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm">Today's Care</CardTitle>
            {selectedDue.size > 0 && (
              <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white"
                onClick={() => startBulkComplete(selectedDue, dueToday)}
                data-testid="global-due-complete-selected">
                <CheckCircle className="w-3 h-3 mr-1" /> Complete Selected ({selectedDue.size})
              </Button>
            )}
          </CardHeader>
          <CardContent className="pt-0 max-h-[400px] overflow-y-auto">
            {loading ? <p className="text-sm text-center py-6 text-muted-foreground">Loading…</p> :
             filteredDue.length === 0 ? <p className="text-sm text-center py-6 text-muted-foreground italic">No care due today.</p> :
             <div className="space-y-2">{filteredDue.map(i => renderCareRow(i, selectedDue.has(i.id), id => toggleSel(selectedDue, setSelectedDue, id)))}</div>}
          </CardContent>
        </Card>

        {/* Mobile Quick Log — 3 buttons per row, shown only on small screens */}
        <Card className="sm:hidden">
          <CardHeader className="py-3"><CardTitle className="text-sm">Quick Log</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {/* Medical — purple */}
              <div className="bg-purple-100 rounded-2xl p-1.5" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px'}}>
                {QL_MEDICAL.map(item => {
                  const Icon = item.icon; const Icon2 = item.icon2;
                  return (
                    <button key={item.key}
                      onClick={() => item.isLayup ? setLayupOpen(true) : setQuickLog({ open: true, prefill: { category: item.category, type: item.type } })}
                      className="flex flex-col items-center justify-center gap-1 p-2 bg-white rounded-xl hover:shadow-md transition-shadow">
                      {Icon2 ? <div className="flex gap-0.5"><Icon className="w-4 h-4 text-primary" /><Icon2 className="w-4 h-4 text-primary" /></div> : <Icon className="w-5 h-5 text-primary" />}
                      <span className="text-[10px] font-bold text-primary text-center leading-tight">{item.label}</span>
                    </button>
                  );
                })}
              </div>
              {/* Daily Care — sky */}
              <div className="bg-sky-100 rounded-2xl p-1.5" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px'}}>
                {QL_DAILY.map(item => {
                  const Icon = item.icon;
                  return (
                    <button key={item.key}
                      onClick={() => item.key === 'turnout' ? setTurnoutOpen(true) : setQuickLog({ open: true, prefill: { category: item.category, type: item.type } })}
                      className="flex flex-col items-center justify-center gap-1 p-2 bg-white rounded-xl hover:shadow-md transition-shadow">
                      <Icon className="w-5 h-5 text-primary" />
                      <span className="text-[10px] font-bold text-primary text-center leading-tight">{item.label}</span>
                    </button>
                  );
                })}
              </div>
              {/* Maintenance (yellow 2/3) + Conditioning (mint 1/3) */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px'}}>
                <div className="bg-yellow-100 rounded-2xl p-1.5" style={{gridColumn:'span 2',display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'6px'}}>
                  {QL_MAINTENANCE.map(item => {
                    const Icon = item.icon;
                    return (
                      <button key={item.key}
                        onClick={() => setQuickLog({ open: true, prefill: { category: item.category, type: item.type } })}
                        className="flex flex-col items-center justify-center gap-1 p-2 bg-white rounded-xl hover:shadow-md transition-shadow">
                        <Icon className="w-5 h-5 text-primary" />
                        <span className="text-[10px] font-bold text-primary text-center leading-tight">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="bg-emerald-100 rounded-2xl p-1.5" style={{display:'grid',gridTemplateColumns:'1fr'}}>
                  {(() => { const Icon = QL_CONDITIONING.icon; return (
                    <button key={QL_CONDITIONING.key}
                      onClick={() => setQuickLog({ open: true, prefill: { category: QL_CONDITIONING.category, type: QL_CONDITIONING.type } })}
                      className="flex flex-col items-center justify-center gap-1 p-2 bg-white rounded-xl hover:shadow-md transition-shadow">
                      <Icon className="w-5 h-5 text-primary" />
                      <span className="text-[10px] font-bold text-primary text-center leading-tight">{QL_CONDITIONING.label}</span>
                    </button>
                  ); })()}
                </div>
              </div>
              {/* Stats / Travel / Other / Note */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px'}}>
                {[...QL_MISC, { key: 'note', label: 'Add Note', icon: NotepadText }].map(item => {
                  const Icon = item.icon;
                  return (
                    <button key={item.key}
                      onClick={async () => {
                        if (item.key === 'travel') { setTravelOpen(true); return; }
                        if (item.key === 'stats')  { setStatsOpen(true); return; }
                        if (item.key === 'note')   { setNoteOpen(true); return; }
                        setQuickLog({ open: true, prefill: item.key === 'other' ? null : { category: item.category, type: item.type } });
                      }}
                      className="flex flex-col items-center justify-center gap-1 p-2 bg-white border rounded-xl hover:shadow-md transition-shadow">
                      <Icon className="w-5 h-5 text-primary" />
                      <span className="text-[10px] font-bold text-primary text-center leading-tight">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="py-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" /> Care in Next 30 Days
              {filteredUpc.length > 0 && <Badge className="bg-primary/10 text-primary text-xs">{filteredUpc.length}</Badge>}
            </CardTitle>
            {selectedUpc.size > 0 && (
              <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90 text-white"
                onClick={() => startBulkComplete(selectedUpc, upcoming)}
                data-testid="global-upc-complete-selected">
                <CheckCircle className="w-3 h-3 mr-1" /> Complete Selected ({selectedUpc.size})
              </Button>
            )}
          </CardHeader>
          <CardContent className="pt-0 max-h-[400px] overflow-y-auto">
            {loading ? <p className="text-sm text-center py-6 text-muted-foreground">Loading…</p> :
             filteredUpc.length === 0 ? <p className="text-sm text-center py-6 text-muted-foreground italic">No upcoming care.</p> :
             <div className="space-y-2">{filteredUpc.map(i => renderCareRow(i, selectedUpc.has(i.id), id => toggleSel(selectedUpc, setSelectedUpc, id)))}</div>}
          </CardContent>
        </Card>
      </div>

      {/* Care Log Table */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Pill className="w-4 h-4 text-primary" /> Care Log
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <CareLogTable
            entries={careLog}
            loading={loading}
            globalMode
            horses={horses}
            locations={locations}
            catalogItems={catalog}
            onBulkPublish={handleBulkPublish}
            layupFilter={layupOnly}
            onLayupFilterClear={() => setLayupOnly(false)}
            onRowClick={entry => setCareLogDetail(entry)}
          />
        </CardContent>
      </Card>

      <QuickLogModal
        open={quickLog.open}
        onClose={() => setQuickLog({ open: false, prefill: null })}
        onSaved={invalidateGlobal}
        catalogItems={catalog}
        locations={locations}
        prefill={quickLog.prefill}
        availableHorses={horses}
        onKeepInCare={async (horseId) => {
          await api.put(`/horses/${horseId}`, { keep_in_care: true });
          invalidateGlobal();
        }}
      />

      <TurnoutLogModal
        open={turnoutOpen}
        onClose={() => setTurnoutOpen(false)}
        onSaved={invalidateGlobal}
        locations={locations}
        availableHorses={horses}
      />

      <TravelLogModal
        open={travelOpen}
        onClose={() => setTravelOpen(false)}
        onSaved={invalidateGlobal}
        locations={locations}
        availableHorses={horses}
      />

      <StatsLogModal
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        onSaved={invalidateGlobal}
        locations={locations}
        availableHorses={horses}
      />

      <LinkedNoteDialog
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        onSaved={() => { invalidateGlobal(); setNoteOpen(false); }}
        availableHorses={horses}
      />

      <LayupModal
        open={layupOpen}
        onClose={() => setLayupOpen(false)}
        onSaved={invalidateGlobal}
        catalogItems={catalog}
        availableHorses={horses}
      />

      <BulkCompleteModal
        open={bulkComplete.open}
        onClose={() => setBulkComplete({ open: false, items: [] })}
        onSaved={() => { setSelectedDue(new Set()); setSelectedUpc(new Set()); invalidateGlobal(); }}
        items={bulkComplete.items}
        horsesById={horsesById}
      />

      <GenerateCareInvoiceModal
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
      />

      <CareLogEntryDetailModal
        open={!!careLogDetail}
        onClose={() => setCareLogDetail(null)}
        entry={careLogDetail}
        locations={locations}
        onDeleted={() => { invalidateGlobal(); setCareLogDetail(null); }}
        onUpdated={(updated) => { invalidateGlobal(); setCareLogDetail(updated); }}
      />

      {/* Layup list dialog */}
      <Dialog open={showLayupList} onOpenChange={setShowLayupList}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Horses on Layup ({activeLayupCount})</DialogTitle>
          </DialogHeader>
          {loadingLayups ? (
            <p className="text-sm text-center py-6 text-muted-foreground">Loading…</p>
          ) : layupList.length === 0 ? (
            <p className="text-sm text-center py-6 text-muted-foreground italic">No active layups found.</p>
          ) : (
            <div className="divide-y max-h-[60vh] overflow-y-auto">
              {layupList.map(layup => {
                const horse = horsesById[layup.horse_id];
                const horseName = horse?.system_name || horse?.name || layup.horse_name || 'Unknown';
                const startDate = layup.start_date ? new Date(layup.start_date) : null;
                const daysOnLayup = startDate
                  ? Math.floor((new Date() - startDate) / 86400000) + 1
                  : null;
                return (
                  <div key={layup.id} className="py-3 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <a
                        href={`/horses/${layup.horse_id}?tab=farmfolio`}
                        className="text-sm font-semibold text-primary hover:underline truncate block"
                        onClick={() => setShowLayupList(false)}
                      >
                        {horseName}
                      </a>
                      {layup.reason && (
                        <p className="text-xs text-muted-foreground mt-0.5">{layup.reason}</p>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      {daysOnLayup !== null && (
                        <span className="inline-block bg-red-100 text-red-700 border border-red-200 text-xs font-semibold px-2 py-0.5 rounded">
                          {daysOnLayup} day{daysOnLayup !== 1 ? 's' : ''}
                        </span>
                      )}
                      {startDate && (
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Since {startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </SidebarLayout>
  );
}
